import { useEffect, useRef, useState } from "react";
import { Button, Form, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { t } from "i18next";
import Helper from "../../core/Helper";
import ApiService from "../../core/service/ApiService";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import { useDataGrid } from "../../core/components/data-grid/DataGridContext";
import FirmSelectList from "../firm/FirmSelectList";
import _ from "lodash";
import CashRegisterSelectList from "../cash-register/CashRegisterSelectList";
import AppointmentTypeSelect from "../appointment/AppointmentTypeSelect";
import { matchesSearch } from "../../core/utils/text-search";
import ExcelButton from "../../core/components/ExcelButton";
import { FieldHolder, MoreFilters, QuickDateRange, QuickSearchBox } from "../../core/components/quick-filter/QuickFilterParts";

/**
 * Kasa hareketleri filtresi — randevu listesindeki çubukla aynı yapı:
 *   1. satır: tarih kartı + hızlı arama
 *   2. satır: randevuyla ortak filtreler (Birim, Kurum, Randevu Türü, Ödeme Yöntemi) + sayfaya özel kart
 *             (Kasa · Tip) + "Diğer filtreler" (Firma, Doktor)
 * Randevudan farkı: arama doluyken tarih aralığı KİLİTLENMEZ ve sunucuda da uygulanır — kasa özetinin
 * geçmiş toplam yetkisi (VIEW_PAST_TOTAL_BALANCE_CASH_VIEW) sorgunun aralığına bakıyor.
 */
export default function CashViewFilter() {
    const methodsRef = useRef([])
    const { queryModel, onFilterPageSort } = useDataGrid();
    const [form] = Form.useForm();
    const { hasPermission } = useUser();

    const [doctors, setDoctors] = useState([])
    const [methods, setMethods] = useState([])
    const [institutions, setInstitutions] = useState([]);
    const [units, setUnits] = useState([])

    const load = async () => {
        const doctorRes = await ApiService.Doctor.GetDoctorList({});
        if (doctorRes.success) {
            setDoctors(doctorRes.data)
        }

        const instRes = await ApiService.Institution.GetInstitutionList(true);
        if (instRes.success) {
            setInstitutions(instRes.data)
        }

        const unitRes = await ApiService.Unit.GetUnitList();
        if (unitRes.success) {
            setUnits(unitRes.data.map(x => ({ ...x, name: `${x?.firm?.name} - ${x.name}` })))
        }

        const res = await ApiService.PaymentMethod.GetPaymentMethodList();
        if (res.success) {
            methodsRef.current = res.data
            setMethods(res.data)
        }
    }

    useEffect(() => {
        form.setFieldsValue(queryModel.filter)
    }, [queryModel.updateKey])

    useEffect(() => {
        load()
    }, [])

    const onFinish = async (formData) => {
        onFilterPageSort({
            filter: {
                ...formData
            }
        })
    }

    const getMethods = () => {
        const group = _.groupBy(methods, (x) => x.firm.name)

        return Object.keys(group).map(key => (
            <Select.OptGroup key={key} label={key}>
                {
                    group[key].map(x => (
                        <Select.Option key={x.id} value={x.id}>
                            {x.name}
                        </Select.Option>
                    ))
                }
            </Select.OptGroup>
        ))
    }

    const toExcel = async () => {
        const res = await ApiService.CashView.ToExcel({
            ...queryModel.filter,
            startDate: Helper.Date.beginOfDay(queryModel.filter.startDate),
            endDate: Helper.Date.endOfDay(queryModel.filter.endDate),
        })

        if (res.success) {
            Helper.File.saveFile({
                blobLink: res.data,
                fileName: res.fileName
            })
        }
    }

    const searchFilterOption = (input, option) => matchesSearch(JSON.stringify(option), input)

    const moreFilters = [
        {
            name: 'firmIdList', label: 'Firm',
            render: () => (
                <FirmSelectList
                    size="small"
                    style={{ width: '100%' }}
                    allowClear={true}
                    mode={'multiple'}
                    maxTagCount={'responsive'}
                    // Firma seçilince ödeme yöntemleri o firmalarınkiyle sınırlanır.
                    onChange={(val) => {
                        if (val?.length > 0) {
                            setMethods(methodsRef.current.filter(x => val.includes(x.firm.id)))
                        }
                        else {
                            setMethods(methodsRef.current)
                        }
                    }}
                />
            )
        },
        {
            name: 'doctorId', label: 'Doctor',
            render: () => <Select size="small" allowClear showSearch style={{ width: '100%' }} fieldNames={{ label: 'name', value: 'id' }} options={doctors} filterOption={searchFilterOption} />
        },
    ]

    return (
        <Form
            form={form}
            size={'small'}
            onFinish={onFinish}
        >
            <Form.Item name={'startDate'} noStyle><FieldHolder /></Form.Item>
            <Form.Item name={'endDate'} noStyle><FieldHolder /></Form.Item>

            <div className="qf-row qf-row-top">
                <QuickDateRange form={form} label={t('Date')} />
                <QuickSearchBox
                    form={form}
                    hint={t('Search by - Patient ID, Name, Phone, Email - Patient Note - Appointment Note, Anamnez, - Accession No etc.')}
                />
            </div>

            <div className="qf-row appt-filter__quick">
                <div className="qf-group appt-filter__field">
                    <Form.Item name={'unitId'} noStyle>
                        <Select size="small" placeholder={t('Unit')} allowClear showSearch style={{ width: '100%' }} fieldNames={{ label: 'name', value: 'id' }} options={units} filterOption={searchFilterOption} />
                    </Form.Item>
                </div>
                <div className="qf-group appt-filter__field">
                    <Form.Item name={'institutionId'} noStyle>
                        <Select size="small" placeholder={t('Institution')} allowClear showSearch style={{ width: '100%' }} fieldNames={{ label: 'name', value: 'id' }} options={institutions} filterOption={searchFilterOption} />
                    </Form.Item>
                </div>
                <div className="qf-group appt-filter__field">
                    <Form.Item name={'appointmentTypeIdList'} noStyle>
                        <AppointmentTypeSelect placeholder={t('Appointment Type')} style={{ width: '100%' }} mode={'multiple'} maxTagCount={'responsive'} allowClear />
                    </Form.Item>
                </div>
                <div className="qf-group appt-filter__field">
                    <Form.Item name={'paymentMethodIdList'} noStyle>
                        <Select size="small" placeholder={t('Payment Method')} mode={'multiple'} maxTagCount={'responsive'} allowClear showSearch style={{ width: '100%' }}>
                            {getMethods()}
                        </Select>
                    </Form.Item>
                </div>

                {/* Sayfaya özel: kasa + gelir/masraf, tek kartta, "Diğer filtreler"in solunda. */}
                <div className="qf-group appt-filter__field appt-filter__field--group" role="group" aria-label={t('Cash Register')}>
                    <Form.Item name={'cashRegisterIdList'} noStyle>
                        <CashRegisterSelectList
                            size="small"
                            placeholder={t('Cash Register')}
                            mode={'multiple'}
                            maxTagCount={'responsive'}
                            all={false}
                            style={{}}
                            onList={(list) => {
                                form.setFieldsValue({
                                    cashRegisterIdList: list?.map(x => x.id)
                                })
                            }}
                        />
                    </Form.Item>
                    <Form.Item name={'type'} noStyle>
                        <Select
                            size="small"
                            placeholder={t('Type')}
                            allowClear={true}
                            options={[
                                { label: t('Revenues'), value: 1 },
                                { label: t('Expense'), value: 2 },
                            ]}
                        />
                    </Form.Item>
                </div>

                <MoreFilters form={form} fields={moreFilters} appliedFilter={queryModel.filter} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Button type={'primary'} htmlType={'submit'} icon={<SearchOutlined />}>
                    {t('Search')}
                </Button>
                {
                    hasPermission(PERMISSIONS.EXPORT_APPOINTMENTS) && (
                        <ExcelButton onClick={toExcel} />
                    )
                }
            </div>
        </Form>
    )
}
